import { useState, useCallback } from 'react';
import useTimerCore from './useTimerCore';
import { Lap } from '../types';

/**
 * Options for lap manager hook
 */
interface LapManagerOptions {
  /** Optional callback when lap is completed */
  onLapCompleted?: (lap: Lap) => void;
  /** Current main timer value from parent */
  mainTime?: number | null;
  /** Timer running status from parent */
  isRunning?: boolean | null;
}

/**
 * Return type for useLapManager hook
 */
interface LapManagerResult {
  /** Array of lap data */
  laps: Lap[];
  /** Current lap time in milliseconds */
  lapTime: number;
  /** Main timer time in milliseconds */
  mainTime: number;
  /** Whether the timer is running */
  isRunning: boolean;
  /** Whether the final lap has been recorded */
  hasFinalLapBeenRecorded: boolean;
  /** Record a lap */
  recordLap: (callback?: (lap: Lap) => void) => Lap | null;
  /** Record the final lap if running */
  recordFinalLap: (callback?: (lap: Lap) => void) => Lap | null;
  /** Reset all lap data */
  resetLaps: () => void;
  /** Start all timers (if using internal timers) */
  startAllTimers: () => void;
  /** Pause all timers (if using internal timers) */
  pauseAllTimers: () => void;
  /** Reset all timers and lap data */
  resetAll: () => void;
  /** Internal timer for direct access */
  internalTimer: ReturnType<typeof useTimerCore>;
  /** Lap timer for direct access */
  lapTimer: ReturnType<typeof useTimerCore>;
}

/**
 * Custom hook for lap management functionality
 *
 * @param options - Configuration options
 * @returns Lap management state and methods
 */
const useLapManager = ({
  onLapCompleted,
  mainTime: externalMainTime = null,
  isRunning: externalIsRunning = null,
}: LapManagerOptions = {}): LapManagerResult => {
  // Create internal timer if external time is not provided
  const internalTimer = useTimerCore();

  // Use either external values or internal timer values
  const mainTime = externalMainTime !== null ? externalMainTime : internalTimer.time;
  const isRunning = externalIsRunning !== null ? externalIsRunning : internalTimer.isRunning;

  // Lap state
  const [laps, setLaps] = useState<Lap[]>([]);
  const [hasFinalLapBeenRecorded, setHasFinalLapBeenRecorded] = useState<boolean>(false);

  // Use a separate timer for lap timing
  const lapTimer = useTimerCore();

  /**
   * Record a lap
   * @returns Lap data that was recorded
   */
  const recordLap = useCallback(
    (callback?: (lap: Lap) => void): Lap | null => {
      if (isRunning) {
        // Always use the internal lap count for the sequence
        const lapNumber = laps.length + 1;
        const currentLapTime = lapTimer.time;
        const currentTotalTime = mainTime;

        // Create lap data object
        const lapData: Lap = {
          id: String(Date.now()),
          number: lapNumber,
          time: currentLapTime,
          cumulativeTime: currentTotalTime,
          timestamp: Date.now(),
          isActive: true,
        };

        // Record the current lap time
        setLaps(prevLaps => [...prevLaps, lapData]);

        // Notify parent component about the lap if callback provided
        if (callback || onLapCompleted) {
          const cb = callback || onLapCompleted;
          cb?.(lapData);
        }

        // Reset lap timer and start a new one
        lapTimer.resetTimer();

        // Start a new lap timer session
        setTimeout(() => {
          lapTimer.startNewTimer();
        }, 0);

        return lapData;
      }
      return null;
    },
    [isRunning, laps.length, lapTimer, mainTime, onLapCompleted]
  );

  /**
   * Record the final lap if running
   * @returns Lap data if recorded, null otherwise
   */
  const recordFinalLap = useCallback(
    (callback?: (lap: Lap) => void): Lap | null => {
      // Only record the final lap if we haven't already and there's an active lap
      if (isRunning && lapTimer.time > 0 && !hasFinalLapBeenRecorded) {
        const lapNumber = laps.length + 1;
        const currentLapTime = lapTimer.time;
        const currentTotalTime = mainTime;

        // Create lap data object for the final lap
        const lapData: Lap = {
          id: String(Date.now()),
          number: lapNumber,
          time: currentLapTime,
          cumulativeTime: currentTotalTime,
          timestamp: Date.now(),
          isActive: false,
        };

        // Record the current lap time
        setLaps(prevLaps => [...prevLaps, lapData]);

        // Notify parent component about the lap
        if (callback || onLapCompleted) {
          const cb = callback || onLapCompleted;
          cb?.(lapData);
        }

        // Mark that we've recorded the final lap
        setHasFinalLapBeenRecorded(true);

        return lapData;
      }

      return null;
    },
    [isRunning, lapTimer.time, mainTime, laps.length, hasFinalLapBeenRecorded, onLapCompleted]
  );

  /**
   * Reset all lap data
   */
  const resetLaps = useCallback(() => {
    setLaps([]);
    setHasFinalLapBeenRecorded(false);
    lapTimer.resetTimer();
  }, [lapTimer]);

  /**
   * Start all timers (if using internal timers)
   */
  const startAllTimers = useCallback(() => {
    if (externalMainTime === null) {
      internalTimer.startTimer();
    }
    lapTimer.startTimer();
    setHasFinalLapBeenRecorded(false);
  }, [externalMainTime, internalTimer, lapTimer]);

  /**
   * Pause all timers (if using internal timers)
   */
  const pauseAllTimers = useCallback(() => {
    if (externalMainTime === null) {
      internalTimer.pauseTimer();
    }
    lapTimer.pauseTimer();
  }, [externalMainTime, internalTimer, lapTimer]);

  /**
   * Reset all timers and lap data
   */
  const resetAll = useCallback(() => {
    if (externalMainTime === null) {
      internalTimer.resetTimer();
    }
    lapTimer.resetTimer();
    resetLaps();
  }, [externalMainTime, internalTimer, lapTimer, resetLaps]);

  return {
    // Lap states
    laps,
    lapTime: lapTimer.time,
    mainTime: externalMainTime !== null ? externalMainTime : internalTimer.time,
    isRunning: externalIsRunning !== null ? externalIsRunning : internalTimer.isRunning,
    hasFinalLapBeenRecorded,

    // Lap methods
    recordLap,
    recordFinalLap,
    resetLaps,

    // Timer control methods (only if using internal timers)
    startAllTimers,
    pauseAllTimers,
    resetAll,

    // Expose internal timers for direct access if needed
    internalTimer,
    lapTimer,
  };
};

export default useLapManager;
